<template>
  <v-container>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col v-if="$can('user:read')" cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>
    <v-fade-transition>
      <v-row v-if="!isLoading">
        <!-- Basic Info -->
        <v-col cols="12">
          <v-card flat>
            <v-card-title class="d-flex align-center">
              <span>Informações básicas</span>
              <v-spacer />
              <v-btn
                v-if="$can('company:update')"
                color="primary"
                variant="tonal"
                @click="onEditTenant"
              >
                Editar
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Razão Social
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ company.razao_social }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Nome Fantasia
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ company.nome_fantasia }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">CNPJ</div>
                  <div class="text-body-1 mt-1">
                    {{ formatCnpj(company.cnpj) }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">Telefone</div>
                  <div class="text-body-1 mt-1">
                    {{ company.phone || "N/A" }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Inscrição Estadual
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ company.state_inscription || "N/A" }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Inscrição Municipal
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ company.municipal_inscription || "N/A" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Account status -->
        <v-col cols="12">
          <v-card flat>
            <v-card-title class="d-flex align-center">
              <span>Situação da conta</span>
              <v-chip
                :color="isDisabled ? 'error' : 'success'"
                variant="tonal"
                size="small"
                class="ml-3"
              >
                {{ isDisabled ? "Desativada" : "Ativa" }}
              </v-chip>
              <v-spacer />
              <v-btn
                v-if="$can('company:update')"
                :color="isDisabled ? 'primary' : 'error'"
                variant="tonal"
                :loading="isSavingStatus"
                @click="openStatusDialog"
              >
                {{ isDisabled ? "Reativar cliente" : "Desativar cliente" }}
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row v-if="isDisabled">
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Desativada em
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ formatDateTime(status.disabled_utc) }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Desativada por
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ status.disabled_by_user_email || "N/A" }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">Motivo</div>
                  <div class="text-body-1 mt-1">
                    {{ status.disabled_reason || "N/A" }}
                  </div>
                </v-col>
              </v-row>
              <div v-else class="text-body-2 text-medium-emphasis">
                As consultas e os lançamentos estão liberados.
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Address -->
        <v-col cols="12">
          <v-card flat>
            <v-card-title>Endereço</v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <div class="text-caption text-medium-emphasis">CEP</div>
                  <div class="text-body-1 mt-1">{{ company.cep }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="5">
                  <div class="text-caption text-medium-emphasis">
                    Logradouro
                  </div>
                  <div class="text-body-1 mt-1">{{ company.street }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <div class="text-caption text-medium-emphasis">Número</div>
                  <div class="text-body-1 mt-1">{{ company.number }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <div class="text-caption text-medium-emphasis">Estado</div>
                  <div class="text-body-1 mt-1">{{ company.state }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">Bairro</div>
                  <div class="text-body-1 mt-1">
                    {{ company.neighborhood }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">Cidade</div>
                  <div class="text-body-1 mt-1">{{ company.city }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text-medium-emphasis">
                    Complemento
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ company.complement || "N/A" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Technical Info -->
        <v-col cols="12">
          <v-card flat>
            <v-card-title>Informações técnicas</v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <div class="text-caption text-medium-emphasis mb-1">
                    Client Id Público
                  </div>
                  <v-sheet
                    rounded
                    color="surface-variant"
                    class="px-3 py-2 d-inline-flex align-center ga-2"
                  >
                    <code class="text-body-2">{{
                      company.public_client_id
                    }}</code>
                    <v-btn
                      size="x-small"
                      icon
                      variant="text"
                      color="primary"
                      @click.stop="onCopyPublicId(company.public_client_id)"
                    >
                      <v-icon :icon="mdiContentCopy" size="16" />
                    </v-btn>
                  </v-sheet>
                </v-col>
                <v-col
                  v-if="company.custody_scopes && company.custody_scopes.length"
                  cols="12"
                >
                  <div class="text-caption text-medium-emphasis mb-2">
                    Escopos de Custódia
                  </div>
                  <custody-scopes
                    :model-value="company.custody_scopes"
                    :read-only="true"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Administrators -->
        <v-col v-if="$can('user:read')" cols="12">
          <v-card flat>
            <v-card-title>Administradores</v-card-title>
            <v-divider />
            <v-list lines="one" bg-color="transparent">
              <v-list-item
                v-for="user in users"
                :key="user.id"
                :title="user.email"
              >
                <template v-if="!user.is_email_confirmed" v-slot:append>
                  <v-btn
                    size="small"
                    color="primary"
                    variant="text"
                    @click.stop="resendConfirmation(user)"
                    :disabled="isResending"
                  >
                    Reenviar confirmação
                    <v-icon end :icon="mdiEmail" />
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>

    <v-dialog v-model="statusDialog" max-width="480">
      <v-card>
        <v-card-title>
          {{ isDisabled ? "Reativar cliente" : "Desativar cliente" }}
        </v-card-title>
        <v-card-text>
          <p v-if="isDisabled" class="text-body-2">
            O cliente volta a lançar documentos. A data, o motivo e o autor da
            desativação deixam de ser registrados.
          </p>
          <template v-else>
            <p class="text-body-2">
              As consultas continuam disponíveis, e nada pode ser criado ou
              alterado. Os dispositivos do cliente param de custodiar
              documentos.
            </p>
            <v-text-field
              v-model="statusReason"
              label="Motivo"
              counter="255"
              maxlength="255"
              class="mt-4"
            />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="statusDialog = false"> Cancelar </v-btn>
          <v-btn
            :color="isDisabled ? 'primary' : 'error'"
            variant="flat"
            :loading="isSavingStatus"
            @click="confirmStatusChange"
          >
            {{ isDisabled ? "Reativar" : "Desativar" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue";
import { mdiContentCopy, mdiEmail } from "@mdi/js";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useRoute, useRouter } from "vue-router";
import CustodyScopes from "@/components/CustodyScopes.vue";
import { useCompanyQuery } from "@/queries/modules/companies";
import {
  useTenantStatusQuery,
  useSetTenantStatusMutation,
} from "@/queries/modules/tenants";
import {
  useUsersQuery,
  useResendConfirmationMutation,
} from "@/queries/modules/users";
import { useSnackbar } from "@/plugins/snackbar";
import { useBreadcrumbTitle } from "@/composables/useBreadcrumbs";

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const $can = inject("$can");

// Refs for processed company data
const company = ref({
  hubType: null,
  razao_social: "",
  nome_fantasia: "",
  cnae: "",
  phone: "",
  cnpj: "",
  state_inscription: "",
  municipal_inscription: "",
  csc: "",
  id_csc: "",
  cep: "",
  neighborhood: "",
  street: "",
  city: "",
  complement: "",
  state: "",
  number: "",
  company_type: null,
  codigo_municipio: "",
  public_client_id: "",
  custody_scopes: [],
});

// Computed
const tenantId = computed(() => {
  return route.params.tenant_id ? Number(route.params.tenant_id) : null;
});

// TanStack Query - fetch company
const {
  data: companyData,
  isLoading: isLoadingCompany,
  error: companyError,
} = useCompanyQuery(tenantId);

// TanStack Query - the tenant's disabled state, which only the vendor changes
const { data: statusData, error: statusError } = useTenantStatusQuery(tenantId);
const setStatusMutation = useSetTenantStatusMutation();

const status = computed(() => statusData.value || {});
const isDisabled = computed(() => !!status.value.disabled_utc);
const isSavingStatus = computed(() => setStatusMutation.isPending.value);

const statusDialog = ref(false);
const statusReason = ref("");

// Users query params
const usersParams = computed(() => ({
  tenant_id: tenantId.value,
  role_type: "primary",
  is_master: true,
  limit: 10,
  offset: 0,
}));

// TanStack Query - fetch users (only if user has permission)
const {
  data: usersData,
  isLoading: isLoadingUsers,
  error: usersError,
} = useUsersQuery(usersParams);

// TanStack Query - resend confirmation mutation
const resendConfirmationMutation = useResendConfirmationMutation();
const isResending = computed(() => resendConfirmationMutation.isPending.value);

// Computed properties
const isLoading = computed(
  () => isLoadingCompany.value || isLoadingUsers.value,
);
const users = computed(() => usersData.value?.items || []);

const tenantName = computed(() => companyData.value?.razao_social);
useBreadcrumbTitle(tenantName, isLoadingCompany);

// Format CNPJ helper
const formatCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

// Watch company data to populate local state
watch(
  companyData,
  (data) => {
    if (data) {
      company.value = {
        company_type: data.company_type,
        razao_social: data.razao_social,
        nome_fantasia: data.nome_fantasia,
        phone: data.phone,
        cnpj: data.cnpj,
        state_inscription: data.state_inscription,
        sefazKey: data.sefaz_security_key,
        cnae: data.cnae,
        csc: data.csc,
        id_csc: data.id_csc,
        municipal_inscription: data.municipal_inscription,
        cep: data.zipcode,
        street: data.street,
        number: data.number,
        neighborhood: data.neighborhood,
        city: data.city,
        state: data.uf,
        codigo_municipio: data.codigo_municipio,
        complement: data.complement || "",
        public_client_id: data.public_client_id,
        custody_scopes: data.custody_scopes || ["custody"],
      };
    }
  },
  { immediate: true },
);

// Watch for errors
watch([companyError, usersError, statusError], ([compErr, userErr, statErr]) => {
  if (compErr) {
    snackbar.error(compErr.message);
  }
  if (userErr) {
    snackbar.error(userErr.message);
  }
  if (statErr) {
    snackbar.error(statErr.message);
  }
});

// Methods
const onEditTenant = () => {
  router.push({
    name: "tenant.edit",
    params: {
      tenant_id: tenantId.value,
    },
  });
};

const onCopyPublicId = (publicId) => {
  navigator.clipboard.writeText(publicId);
  snackbar.success("Client Id copiado para área de transferência.");
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
};

const openStatusDialog = () => {
  // The stored reason is the vendor's note about why this customer was turned
  // off, not a default for turning the next one off: a dialog that opens
  // pre-filled invites re-submitting a stale reason.
  statusReason.value = "";
  statusDialog.value = true;
};

const confirmStatusChange = async () => {
  const disabling = !isDisabled.value;
  try {
    await setStatusMutation.mutateAsync({
      tenantId: tenantId.value,
      disabled: disabling,
      reason: disabling ? statusReason.value : null,
    });
    statusDialog.value = false;
    snackbar.success(
      disabling ? "Cliente desativado." : "Cliente reativado.",
    );
  } catch (e) {
    snackbar.error(
      e.message || "Ocorreu um erro ao alterar a situação do cliente.",
    );
  }
};

const resendConfirmation = async (user) => {
  try {
    await resendConfirmationMutation.mutateAsync(user.id);
    snackbar.success(
      "O link de confirmação foi enviado para o email do usuário.",
    );
  } catch (e) {
    snackbar.error(
      e.message ||
        "Ocorreu um erro ao enviar o link de confirmação para o email, tente novamente.",
    );
  }
};
</script>
